import * as React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { ButtonProps, buttonVariants } from '@/components/ui/button';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => {
  const { t } = useTranslation();
  return (
    <nav
      role="navigation"
      aria-label={t('common.pagination')}
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
};
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => <li ref={ref} className={className} {...props} />,
);
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
  href?: string;
} & Pick<ButtonProps, 'size'> &
  Omit<React.ComponentProps<'button'>, 'size'>;

/**
 * shadcn ships this as an anchor. Table pagination is not navigation — there is
 * no URL for "page 3 of this table" — so it renders a button unless given an
 * href, which keeps it usable for both.
 */
const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  href,
  ...props
}: PaginationLinkProps) => {
  const shared = cn(
    buttonVariants({ variant: isActive ? 'outline' : 'ghost', size }),
    'disabled:pointer-events-none disabled:opacity-50',
    className,
  );

  if (href) {
    return (
      <a
        href={href}
        aria-current={isActive ? 'page' : undefined}
        className={shared}
        {...(props as React.ComponentProps<'a'>)}
      >
        {props.children}
      </a>
    );
  }

  return <button type="button" aria-current={isActive ? 'page' : undefined} className={shared} {...props} />;
};
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink size={children ? 'default' : 'icon'} className={cn('gap-1', className)} {...props}>
    <ChevronLeft className="size-4" />
    {children}
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink size={children ? 'default' : 'icon'} className={cn('gap-1', className)} {...props}>
    {children}
    <ChevronRight className="size-4" />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span aria-hidden className={cn('grid size-9 place-items-center', className)} {...props}>
    <MoreHorizontal className="size-4" />
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
